import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  Edit,
  Eye,
  Grid3X3,
  LayoutGrid,
  List,
  Map,
  MapPin,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Table2,
  Users,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setProjectId } from '../store/slices/projectSlice';
import { ProjectsDataGrid } from '../components/projects/ProjectsDataGrid';
import ProjectEditDrawer from '../components/projects/ProjectEditDrawer';
import ProjectMapView from '../components/ProjectMapView';
import { projectService } from '../services/projectService';
import { Project, ProjectListParams, ProjectListResponse, ProjectPriority } from '../types';

type ViewMode = 'datagrid' | 'box' | 'map';

const AllProjects: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // UI State
  const [viewMode, setViewMode] = useState<ViewMode>('datagrid');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Data State
  const [projectsData, setProjectsData] = useState<ProjectListResponse | null>(null);

  // Edit Drawer State
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);

  // Delete Confirmation State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  const [deletingProjectName, setDeletingProjectName] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter State - shared across all views
  const [filters, setFilters] = useState<ProjectListParams>({
    page: 1,
    limit: 25,
    search: '',
    status: 'all',
    priority: 'all',
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  });

  // Handle success message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, [location.state]);

  // Load projects when filters change
  useEffect(() => {
    loadProjects();
  }, [filters]);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const response = await projectService.getProjects(filters);
      if (response.success && response.data) {
        setProjectsData(response.data);
        setErrorMessage(null);
      } else {
        setErrorMessage(response.message || 'Failed to load projects');
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setErrorMessage('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    loadProjects();
  };

  const handleFilterChange = (key: keyof ProjectListParams, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : typeof value === 'number' ? value : 1,
    }));
  };

  // Handle project selection - works for all views
  const handleProjectSelect = (projectOrId: string | Project) => {
    const projectId =
      typeof projectOrId === 'string' ? projectOrId : projectOrId.id || projectOrId._id;
    dispatch(setProjectId(projectId));
    navigate(`/projects/${projectId}/dashboard`);
  };

  // Handle project update from DataGrid
  const handleProjectUpdate = async (projectId: string, updates: Partial<Project>) => {
    try {
      const response = await projectService.updateProject(
        projectId,
        updates as unknown as Parameters<typeof projectService.updateProject>[1]
      );
      if (response.success) {
        loadProjects();
        setSuccessMessage('Project updated successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setErrorMessage(response.message || 'Failed to update project');
        setTimeout(() => setErrorMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error updating project:', error);
      setErrorMessage('Failed to update project');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  // Handle sort change from DataGrid
  const handleSortFromGrid = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder,
      page: 1,
    }));
  };

  // Handle filter change from DataGrid
  const handleFilterFromGrid = (gridFilters: ProjectListParams) => {
    setFilters(prev => ({
      ...prev,
      ...gridFilters,
      page: 1,
    }));
  };

  // Handle page change from DataGrid
  const handlePageChangeFromGrid = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // Handle page size change from DataGrid
  const handlePageSizeChangeFromGrid = (pageSize: number) => {
    setFilters(prev => ({ ...prev, limit: pageSize, page: 1 }));
  };

  // Handle edit project
  const handleProjectEdit = (projectId: string) => {
    setEditingProjectId(projectId);
    setIsEditDrawerOpen(true);
  };

  // Handle edit save
  const handleEditSave = (_updatedProject: Project) => {
    loadProjects();
    setSuccessMessage('Project updated successfully');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Handle delete project click
  const handleProjectDelete = (projectId: string) => {
    const project = projects.find(p => (p.id || p._id) === projectId);
    if (project) {
      setDeletingProjectId(projectId);
      setDeletingProjectName(project.name);
      setIsDeleteModalOpen(true);
    }
  };

  // Confirm delete project
  const confirmDeleteProject = async () => {
    if (!deletingProjectId) return;

    setIsDeleting(true);
    try {
      const response = await projectService.deleteProject(deletingProjectId);
      if (response.success) {
        setSuccessMessage('Project deleted successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        loadProjects();
      } else {
        setErrorMessage(response.message || 'Failed to delete project');
        setTimeout(() => setErrorMessage(null), 5000);
      }
    } catch {
      setErrorMessage('Failed to delete project');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setDeletingProjectId(null);
      setDeletingProjectName('');
    }
  };

  // Handle duplicate project
  const handleProjectDuplicate = async (projectId: string) => {
    try {
      const projectResponse = await projectService.getProject(projectId);
      if (projectResponse.success && projectResponse.data) {
        const project = projectResponse.data;
        const duplicateData = {
          name: `${project.name} (Copy)`,
          description: project.description,
          status: 'planning' as const,
          priority: project.priority,
          startDate: new Date(),
          endDate: project.endDate ? new Date(project.endDate) : undefined,
          budget: project.budget,
          location: project.location,
        };

        const createResponse = await projectService.createProject(
          duplicateData as unknown as Parameters<typeof projectService.createProject>[0]
        );
        if (createResponse.success) {
          setSuccessMessage('Project duplicated successfully');
          setTimeout(() => setSuccessMessage(null), 3000);
          loadProjects();
        } else {
          setErrorMessage(createResponse.message || 'Failed to duplicate project');
          setTimeout(() => setErrorMessage(null), 5000);
        }
      }
    } catch {
      setErrorMessage('Failed to duplicate project');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  // Get projects from API response
  const projects = projectsData?.projects || [];

  // Helper functions for Box view
  const getPriorityColor = (priority: ProjectPriority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-500';
      case 'high':
        return 'bg-red-400';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: Date | string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'on-track':
        return 'text-green-700 bg-green-100';
      case 'on_hold':
      case 'behind':
        return 'text-amber-700 bg-amber-100';
      case 'completed':
        return 'text-blue-700 bg-blue-100';
      case 'cancelled':
        return 'text-red-700 bg-red-100';
      case 'planning':
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'on-track':
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'on_hold':
      case 'behind':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  // Box/Card View Component
  const ProjectCard = ({ project }: { project: Project }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Project Image */}
      <div className="h-40 bg-gradient-to-br from-brand-100 to-brand-200 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-14 h-14 bg-brand-500 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-white font-bold text-lg">{project.name.charAt(0)}</span>
            </div>
            <p className="text-sm text-brand-700 font-medium">
              {project.location?.city && project.location?.state
                ? `${project.location.city}, ${project.location.state}`
                : project.location?.address || 'Location not specified'}
            </p>
          </div>
        </div>
        <div className="absolute top-3 right-3">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${getPriorityColor(project.priority)}`}
          >
            {project.priority.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">{project.name}</h3>
            <p className="text-sm text-gray-600 line-clamp-2 mt-1">{project.description}</p>
          </div>
          <button
            onClick={() => handleProjectEdit(project.id || project._id || '')}
            className="ml-2 p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>
        </div>

        {/* Status */}
        <div className="flex items-center justify-between mb-3">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}
          >
            {getStatusIcon(project.status)}
            <span className="ml-1 capitalize">{project.status.replace('_', ' ')}</span>
          </span>
          <p className="text-xs text-gray-500">
            Updated {project.updatedAt ? formatDate(project.updatedAt) : 'N/A'}
          </p>
        </div>

        {/* Progress */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium">{project.progress || 0}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-brand-500 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${project.progress || 0}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>
              {project.tasksCompleted || 0}/{project.totalTasks || 0} tasks
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
          <div>
            <div className="flex items-center text-gray-500 mb-0.5">
              <DollarSign className="h-3.5 w-3.5 mr-1" />
              <span className="text-xs">Budget</span>
            </div>
            <p className="font-medium text-sm">{formatCurrency(project.budget)}</p>
          </div>
          <div>
            <div className="flex items-center text-gray-500 mb-0.5">
              <Users className="h-3.5 w-3.5 mr-1" />
              <span className="text-xs">Team</span>
            </div>
            <p className="font-medium text-sm">{project.teamSize || 0} members</p>
          </div>
        </div>

        {/* Deadline */}
        <div className="flex items-center justify-between text-sm mb-4">
          <div className="flex items-center text-gray-500">
            <Calendar className="h-3.5 w-3.5 mr-1" />
            <span className="text-xs">Deadline</span>
          </div>
          <span className="font-medium text-sm">
            {project.endDate ? formatDate(project.endDate) : 'N/A'}
          </span>
        </div>

        {/* Actions */}
        <div className="flex space-x-2">
          <button
            onClick={() => handleProjectSelect(project)}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <Eye className="h-4 w-4 mr-2" />
            View
          </button>
          <button
            onClick={() => handleProjectEdit(project.id || project._id || '')}
            className="px-3 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors inline-flex items-center"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto">
        {/* Success Message */}
        {successMessage && (
          <div className="mx-6 mt-6 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800 font-medium">{successMessage}</span>
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="ml-auto text-green-600 hover:text-green-800 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="mx-6 mt-6 mb-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-red-800 font-medium">{errorMessage}</span>
                <button
                  onClick={() => setErrorMessage(null)}
                  className="ml-auto text-red-600 hover:text-red-800 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">All Projects</h1>
              <p className="text-gray-600 text-sm mt-0.5">
                Manage and track all your construction projects
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 transition-colors"
                title="Refresh projects"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>

              <button
                onClick={() => navigate('/projects/create')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Project
              </button>
            </div>
          </div>
        </div>

        {/* Shared Filter Bar */}
        <div className="px-6 py-4 bg-white border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={filters.search || ''}
                  onChange={e => handleFilterChange('search', e.target.value)}
                  className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>

            {/* Filters and View Toggle */}
            <div className="flex items-center space-x-3">
              {/* Status Filter */}
              <select
                value={filters.status || 'all'}
                onChange={e => handleFilterChange('status', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
              >
                <option value="all">All Status</option>
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>

              {/* Priority Filter */}
              <select
                value={filters.priority || 'all'}
                onChange={e => handleFilterChange('priority', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
              >
                <option value="all">All Priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>

              {/* Sort */}
              <select
                value={filters.sortBy || 'updatedAt'}
                onChange={e => handleFilterChange('sortBy', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
              >
                <option value="name">Sort by Name</option>
                <option value="status">Sort by Status</option>
                <option value="priority">Sort by Priority</option>
                <option value="startDate">Sort by Start Date</option>
                <option value="endDate">Sort by End Date</option>
                <option value="budget">Sort by Budget</option>
                <option value="createdAt">Sort by Created</option>
                <option value="updatedAt">Sort by Updated</option>
              </select>

              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('datagrid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'datagrid'
                      ? 'bg-white text-brand-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="DataGrid View"
                >
                  <Table2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('box')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'box'
                      ? 'bg-white text-brand-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Box/Card View"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'map'
                      ? 'bg-white text-brand-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Map View"
                >
                  <Map className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6">
          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-8 w-8 text-brand-500 animate-spin" />
            </div>
          )}

          {/* DataGrid View */}
          {!isLoading && viewMode === 'datagrid' && (
            <ProjectsDataGrid
              projects={projects}
              loading={isLoading}
              error={errorMessage || undefined}
              totalCount={projectsData?.total || 0}
              currentPage={filters.page || 1}
              pageSize={filters.limit || 25}
              onPageChange={handlePageChangeFromGrid}
              onPageSizeChange={handlePageSizeChangeFromGrid}
              onSort={handleSortFromGrid}
              onFilter={handleFilterFromGrid}
              onProjectUpdate={handleProjectUpdate}
              onProjectSelect={handleProjectSelect}
              onProjectEdit={handleProjectEdit}
              onProjectDelete={handleProjectDelete}
              onProjectDuplicate={handleProjectDuplicate}
              permissions={{
                canEdit: true,
                canDelete: true,
                canCreateView: true,
                canExport: true,
              }}
            />
          )}

          {/* Box/Card View */}
          {!isLoading && viewMode === 'box' && (
            <>
              {projects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {projects.map(project => (
                    <ProjectCard key={project.id || project._id} project={project} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
                  <p className="text-gray-500 mb-6">
                    {filters.search || filters.status !== 'all' || filters.priority !== 'all'
                      ? "Try adjusting your filters to find what you're looking for."
                      : 'Get started by creating your first project.'}
                  </p>
                  {!filters.search && filters.status === 'all' && filters.priority === 'all' && (
                    <button
                      onClick={() => navigate('/projects/create')}
                      className="inline-flex items-center px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Project
                    </button>
                  )}
                </div>
              )}

              {/* Pagination for Box View */}
              {projects.length > 0 &&
                projectsData &&
                projectsData.total > (filters.limit || 25) && (
                  <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                    <div className="text-sm text-gray-700">
                      Showing {((filters.page || 1) - 1) * (filters.limit || 25) + 1} to{' '}
                      {Math.min((filters.page || 1) * (filters.limit || 25), projectsData.total)} of{' '}
                      {projectsData.total} projects
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleFilterChange('page', (filters.page || 1) - 1)}
                        disabled={(filters.page || 1) <= 1}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handleFilterChange('page', (filters.page || 1) + 1)}
                        disabled={(filters.page || 1) * (filters.limit || 25) >= projectsData.total}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
            </>
          )}

          {/* Map View */}
          {!isLoading && viewMode === 'map' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <ProjectMapView projects={projects} onProjectSelect={handleProjectSelect} />
            </div>
          )}
        </div>
      </div>

      {/* Edit Project Drawer */}
      <ProjectEditDrawer
        projectId={editingProjectId}
        isOpen={isEditDrawerOpen}
        onClose={() => {
          setIsEditDrawerOpen(false);
          setEditingProjectId(null);
        }}
        onSave={handleEditSave}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-[100] animate-in fade-in duration-200"
              onClick={() => !isDeleting && setIsDeleteModalOpen(false)}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="bg-red-100 p-2 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Delete Project</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => !isDeleting && setIsDeleteModalOpen(false)}
                    disabled={isDeleting}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-6 py-4">
                  <p className="text-gray-600">
                    Are you sure you want to delete{' '}
                    <span className="font-semibold text-gray-900">"{deletingProjectName}"</span>?
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    This action cannot be undone. All project data, tasks, and associated files will
                    be permanently removed.
                  </p>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                  <button
                    type="button"
                    onClick={() => setIsDeleteModalOpen(false)}
                    disabled={isDeleting}
                    className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDeleteProject}
                    disabled={isDeleting}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Project'}
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default AllProjects;
